import * as THREE from "three";

const canvas = document.getElementById("stage3d");
if (!canvas) throw new Error("no 3D stage on this page");

/* Colours: oil sampled from the tatvamazi bottle render, twine sampled from the tag photo, gold from Logo.png */
const OIL_LIGHT = new THREE.Color("#E5DDB5"), OIL_DEEP = new THREE.Color("#D9BA60");
const GOLD = new THREE.Color("#F8BE71");
const ROPE = ["#744219", "#A87F5E", "#F4C68F"];
const PAPER = new THREE.Color("#F3D7B2");
const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
document.documentElement.classList.add("gl");
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
renderer.outputColorSpace = THREE.SRGBColorSpace;

const scene = new THREE.Scene();

/* ---------- Studio lighting: a black room with softboxes, used for all reflections ---------- */
function studioEnvironment() {
  const env = new THREE.Scene();
  env.background = new THREE.Color(0x000000);
  const box = (w, h, k, color, pos, look) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(k), side: THREE.DoubleSide }));
    m.position.set(...pos); m.lookAt(...look); env.add(m);
  };
  box(10, 6, 1.3, 0xffffff, [0, 9, 2], [0, 0, 0]);          // overhead softbox
  box(1.4, 12, 2.6, 0xffffff, [-7, 1, 3], [0, 0, 0]);       // tall strip, left
  box(0.8, 12, 1.6, 0xffffff, [6.5, 1, 4], [0, 0, 0]);      // thin strip, right
  box(8, 8, 1.4, "#F8BE71", [0, 0, -9], [0, 0, 0]);         // warm panel behind
  box(6, 2, 0.6, 0xffffff, [0, -6, 6], [0, 0, 0]);          // low front fill
  const pmrem = new THREE.PMREMGenerator(renderer);
  const tex = pmrem.fromScene(env, 0.02).texture;
  pmrem.dispose();
  return tex;
}
scene.environment = studioEnvironment();

const FOV = 30, CAM_Z = 10;
const camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 100);
camera.position.set(0, 0, CAM_Z);
const key = new THREE.DirectionalLight(0xffffff, 1.2); key.position.set(-4, 6, 6); scene.add(key);
const rim = new THREE.DirectionalLight(GOLD, 1.4); rim.position.set(5, 3, -5); scene.add(rim);
scene.add(new THREE.AmbientLight(0xffffff, 0.15));

function gradientTexture(w, h, cx, cy, r, stops) {
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  const g = c.getContext("2d"), gr = g.createRadialGradient(cx, cy, 0, cx, cy, r);
  for (const [o, col] of stops) gr.addColorStop(o, col);
  g.fillStyle = gr; g.fillRect(0, 0, w, h);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}

/* ---------- Bottle (1 unit = 100 px of the reference front view) ---------- */
const pivot = new THREE.Group(); scene.add(pivot);
const bottle = new THREE.Group(); pivot.add(bottle);
const R = 1.53, BODY_TOP = 5.9;

/* Glass: only reflections are added on top (black body + additive blend), so it reads as clear crystal */
const glassPts = [
  [0.001, 0], [R - 0.12, 0], [R - 0.03, 0.03], [R, 0.12], [R, BODY_TOP], [R - 0.02, 5.97], [R - 0.12, 6.03],
  [1.0, 6.07], [0.8, 6.1], [0.74, 6.14], [0.74, 6.3], [0.66, 6.3], [0.66, 6.12], [0.9, 6.02], [R - 0.14, 5.95],
  [R - 0.1, 5.88], [R - 0.1, 0.2]
].map(([x, y]) => new THREE.Vector2(x, y));
const shellMat = new THREE.MeshPhysicalMaterial({
  color: 0x000000, roughness: 0, metalness: 0, clearcoat: 0.3, clearcoatRoughness: 0.05, envMapIntensity: 0.45,
  transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.FrontSide
});
const glass = new THREE.Mesh(new THREE.LatheGeometry(glassPts, 160), shellMat);
glass.renderOrder = 3; bottle.add(glass);

/* Thin glass floor: the oil fills right down to the bottom */
const BASE_H = 0.14;
const baseGlow = new THREE.Object3D();
const rimMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.22, blending: THREE.AdditiveBlending, depthWrite: false });
for (const [y, rr, o] of [[0.04, R - 0.06, 0.18]]) {
  const ring = new THREE.Mesh(new THREE.TorusGeometry(rr, 0.014, 6, 128), rimMat.clone()); ring.material.opacity = o;
  ring.rotation.x = Math.PI / 2; ring.position.y = y; bottle.add(ring);
}

/* Oil: real transmission with golden absorption. Fills from the floor to the shoulder. */
const OIL_BASE = BASE_H, OIL_H = 6.0 - BASE_H;
/* Oil shading follows the reference render: pale centre, deeper gold toward the edges where light travels through more oil */
const oilMat = new THREE.ShaderMaterial({
  uniforms: {
    uCenter: { value: new THREE.Color("#E5DDB5") }, uEdge: { value: new THREE.Color("#D9BA60") },
    uRim: { value: new THREE.Color("#8F7027") }, uOpacity: { value: 0.93 }
  },
  vertexShader: `varying vec3 vN; varying vec3 vV; varying float vY;
    void main() { vec4 mv = modelViewMatrix * vec4(position, 1.0); vN = normalize(normalMatrix * normal); vV = normalize(-mv.xyz); vY = position.y; gl_Position = projectionMatrix * mv; }`,
  fragmentShader: `uniform vec3 uCenter; uniform vec3 uEdge; uniform vec3 uRim; uniform float uOpacity;
    varying vec3 vN; varying vec3 vV; varying float vY;
    void main() {
      float f = 1.0 - abs(dot(normalize(vN), normalize(vV)));
      vec3 col = mix(uCenter, uEdge, smoothstep(0.05, 0.75, f));
      col = mix(col, uRim, smoothstep(0.7, 1.0, f) * 0.85);
      col *= 0.92 + 0.08 * smoothstep(0.0, 5.8, vY);
      gl_FragColor = vec4(col, uOpacity);
      #include <colorspace_fragment>
    }`,
  transparent: true
});
const oil = new THREE.Mesh(new THREE.CylinderGeometry(R - 0.1, R - 0.1, OIL_H, 128).translate(0, OIL_H / 2, 0), oilMat);
oil.position.y = OIL_BASE; oil.renderOrder = 1; bottle.add(oil);

/* Floor glow under the bottle */
const floor = new THREE.Mesh(
  new THREE.PlaneGeometry(6, 2.2),
  new THREE.MeshBasicMaterial({ map: gradientTexture(256, 96, 128, 48, 128, [[0, "rgba(217,186,96,0.9)"], [1, "rgba(0,0,0,0)"]]), transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, depthWrite: false })
);
floor.rotation.x = -Math.PI / 2; floor.position.y = -3.82; pivot.add(floor);

/* Cap: matte black plastic with rounded edges */
const capMat = new THREE.MeshPhysicalMaterial({ color: 0x050505, roughness: 0.42, metalness: 0, clearcoat: 0.35, clearcoatRoughness: 0.35, envMapIntensity: 1 });
const lathe = (pts, seg = 96) => new THREE.LatheGeometry(pts.map(([x, y]) => new THREE.Vector2(x, y)), seg);
const collar = new THREE.Mesh(lathe([[0.72, 6.08], [0.76, 6.1], [0.76, 6.71], [0.74, 6.73]]), capMat); bottle.add(collar);
const capBase = new THREE.Mesh(lathe([[0.42, 6.73], [1.03, 6.73], [1.08, 6.76], [1.09, 6.8], [1.09, 6.97], [0.42, 6.97]]), capMat); bottle.add(capBase);
const HINGE_Y = 7.0, HINGE_Z = -1.1;
const lidPivot = new THREE.Group(); lidPivot.position.set(0, HINGE_Y, HINGE_Z); bottle.add(lidPivot);
const lid = new THREE.Mesh(lathe([[0.001, 7.0], [1.08, 7.0], [1.09, 7.03], [1.09, 7.52], [1.07, 7.58], [1.02, 7.615], [0.001, 7.62]]), capMat);
lid.position.set(0, -HINGE_Y, -HINGE_Z); lidPivot.add(lid);
const bowShape = new THREE.Shape();
bowShape.moveTo(-0.17, 0.1); bowShape.lineTo(-0.03, 0.03); bowShape.lineTo(0.03, 0.03); bowShape.lineTo(0.17, 0.1);
bowShape.lineTo(0.17, -0.1); bowShape.lineTo(0.03, -0.03); bowShape.lineTo(-0.03, -0.03); bowShape.lineTo(-0.17, -0.1); bowShape.closePath();
const bow = new THREE.Mesh(new THREE.ExtrudeGeometry(bowShape, { depth: 0.05, bevelEnabled: true, bevelThickness: 0.015, bevelSize: 0.012, bevelSegments: 2 }),
  new THREE.MeshPhysicalMaterial({ color: 0x050505, roughness: 0.25, clearcoat: 0.6 }));
const BOW_A = 0.75; bow.position.set(Math.sin(BOW_A) * 1.07, 7.3 - HINGE_Y, Math.cos(BOW_A) * 1.07 - HINGE_Z); bow.rotation.y = BOW_A; lidPivot.add(bow);
const hinge = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.16, 0.12), capMat); hinge.position.set(0, 7.02, -1.1); bottle.add(hinge);

function loadImg(src) { return new Promise((res, rej) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = rej; i.src = src; }); }

/* ---------- Engraved logo: a normal map built from the real logo art, lit only by reflections ---------- */
async function makeEmboss() {
  const [mark, word] = await Promise.all([loadImg(canvas.dataset.mark), loadImg(canvas.dataset.word)]);
  const S = 1024, arc = 2.3, r = R + 0.004, widthU = r * arc, heightU = widthU, px = S / widthU, cy = 3.2;
  const toY = (yU) => (cy + heightU / 2 - yU) * px;
  const c = document.createElement("canvas"); c.width = c.height = S;
  const g = c.getContext("2d");
  g.filter = "blur(1.2px)";
  const mw = 1.96 * px; g.drawImage(mark, (S - mw) / 2, toY(3.8) - mw / 2, mw, mw);
  const ww = 2.1 * px, wh = ww * word.height / word.width; g.drawImage(word, (S - ww) / 2, toY(2.4) - wh / 2, ww, wh);
  const src = g.getImageData(0, 0, S, S).data;
  const hgt = new Float32Array(S * S);
  for (let i = 0; i < S * S; i++) hgt[i] = src[i * 4 + 3] / 255;
  const nC = document.createElement("canvas"), aC = document.createElement("canvas");
  nC.width = nC.height = aC.width = aC.height = S;
  const nI = nC.getContext("2d").createImageData(S, S), aI = aC.getContext("2d").createImageData(S, S);
  for (let y = 1; y < S - 1; y++) for (let x = 1; x < S - 1; x++) {
    const i = y * S + x;
    const dx = (hgt[i - 1] - hgt[i + 1]) * 6, dy = (hgt[i + S] - hgt[i - S]) * 6;
    const l = Math.hypot(dx, dy, 1);
    nI.data[i * 4] = (dx / l * 0.5 + 0.5) * 255; nI.data[i * 4 + 1] = (dy / l * 0.5 + 0.5) * 255; nI.data[i * 4 + 2] = (1 / l * 0.5 + 0.5) * 255; nI.data[i * 4 + 3] = 255;
    const a = Math.min(1, Math.hypot(dx, dy) * 0.75 + hgt[i] * 0.08) * 255;
    aI.data[i * 4] = aI.data[i * 4 + 1] = aI.data[i * 4 + 2] = a; aI.data[i * 4 + 3] = 255;
  }
  nC.getContext("2d").putImageData(nI, 0, 0); aC.getContext("2d").putImageData(aI, 0, 0);
  const normalMap = new THREE.CanvasTexture(nC), alphaMap = new THREE.CanvasTexture(aC);
  const panel = new THREE.Mesh(
    new THREE.CylinderGeometry(r, r, heightU, 160, 1, true, -arc / 2, arc),
    new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(OIL_DEEP).multiplyScalar(0.1), roughness: 0.05, clearcoat: 1, clearcoatRoughness: 0,
      normalMap, normalScale: new THREE.Vector2(1.4, 1.4), alphaMap, transparent: true,
      blending: THREE.AdditiveBlending, depthWrite: false, envMapIntensity: 1.3
    })
  );
  panel.position.y = cy; panel.renderOrder = 4; bottle.add(panel);
}
makeEmboss().catch(() => {});

/* ---------- Coir rope: three twisted plies with stray fibres, knotted at the front right ---------- */
function fibreTexture() {
  const c = document.createElement("canvas"); c.width = 128; c.height = 128;
  const g = c.getContext("2d");
  g.fillStyle = ROPE[1]; g.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 320; i++) {
    g.strokeStyle = [ROPE[0], ROPE[2], ROPE[1]][i % 3]; g.globalAlpha = 0.3 + Math.random() * 0.55; g.lineWidth = 0.6 + Math.random() * 1.6;
    const x = Math.random() * 128, y = Math.random() * 128;
    g.beginPath(); g.moveTo(x, y); g.lineTo(x + 12 + Math.random() * 26, y + (Math.random() - 0.5) * 5); g.stroke();
  }
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(10, 1); return t;
}
const fibreTex = fibreTexture();
const coirMat = new THREE.MeshStandardMaterial({ map: fibreTex, bumpMap: fibreTex, bumpScale: 0.8, roughness: 1, metalness: 0 });
const fuzzMat = new THREE.LineBasicMaterial({ color: new THREE.Color(ROPE[2]), transparent: true, opacity: 0.6 });

/* One ply of the rope: spirals around the centre line of the rope */
class PlyCurve extends THREE.Curve {
  constructor(base, radius, turns, phase) {
    super(); this.base = base; this.r = radius; this.turns = turns; this.phase = phase;
    this.frames = base.computeFrenetFrames(240, !!base.closed);
  }
  getPoint(t, target = new THREE.Vector3()) {
    const i = Math.min(240, Math.round(t * 240));
    const a = t * this.turns * Math.PI * 2 + this.phase;
    return target.copy(this.base.getPointAt(t))
      .addScaledVector(this.frames.normals[i], Math.cos(a) * this.r)
      .addScaledVector(this.frames.binormals[i], Math.sin(a) * this.r);
  }
}
function rope(base, thick = 0.08, turnsPerUnit = 7, segs = 200) {
  const g = new THREE.Group(), len = base.getLength();
  for (let k = 0; k < 3; k++) {
    const ply = new PlyCurve(base, thick * 0.5, Math.max(1, Math.round(len * turnsPerUnit)), k * 2 * Math.PI / 3);
    g.add(new THREE.Mesh(new THREE.TubeGeometry(ply, segs, thick * 0.52, 7, !!base.closed), coirMat));
  }
  return g;
}
function fuzz(base, n, reach, from = 0, to = 1) {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const p = base.getPointAt(from + Math.random() * (to - from));
    const d = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize();
    const s = p.clone().addScaledVector(d, 0.05);
    pts.push(s, s.clone().addScaledVector(d, reach * (0.4 + Math.random())).add(new THREE.Vector3(0, -Math.random() * reach * 0.3, 0)));
  }
  return new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), fuzzMat);
}

const twine = new THREE.Group(); bottle.add(twine);
const NECK_R = 0.88, NECK_Y = 6.24, KNOT_A = 0.8;
const loopPts = [];
for (let i = 0; i < 48; i++) { const a = i / 48 * Math.PI * 2; loopPts.push(new THREE.Vector3(Math.sin(a) * NECK_R, NECK_Y + Math.cos(a - KNOT_A) * 0.035, Math.cos(a) * NECK_R)); }
const neckLoop = new THREE.CatmullRomCurve3(loopPts, true);
twine.add(rope(neckLoop, 0.22, 4, 300), fuzz(neckLoop, 220, 0.16));
const KNOT = new THREE.Vector3(Math.sin(KNOT_A) * (NECK_R + 0.16), NECK_Y - 0.02, Math.cos(KNOT_A) * (NECK_R + 0.16));
const knot = new THREE.Mesh(new THREE.TorusKnotGeometry(0.16, 0.085, 120, 12, 2, 3), coirMat);
knot.position.copy(KNOT); knot.rotation.set(0.3, KNOT_A, 0.4); twine.add(knot);
for (const end of [[0.42, -0.4, 0.22], [0.2, -0.55, 0.36]]) {
  const e = KNOT.clone().add(new THREE.Vector3(...end));
  const c = new THREE.CatmullRomCurve3([KNOT.clone(), KNOT.clone().lerp(e, 0.5).add(new THREE.Vector3(0.07, 0.02, 0.06)), e]);
  twine.add(rope(c, 0.15, 6, 70), fuzz(c, 60, 0.2, 0.75, 1));   // frayed tips
}
twine.add(fuzz(new THREE.CatmullRomCurve3([KNOT.clone().add(new THREE.Vector3(-0.15, 0, 0)), KNOT.clone().add(new THREE.Vector3(0.15, 0, 0))]), 80, 0.18));

/* ---------- Paper tag with the real tag artwork ---------- */
const TAG_W = 2.5, TAG_H = TAG_W * 1340 / 640;
const HOLE_U = 237 / 640, HOLE_V = 88 / 1340;
const tagPivot = new THREE.Group();
tagPivot.position.set(1.3, 5.55, R + 0.3);   // where the hole hangs, in front and to the right
tagPivot.rotation.y = -0.45;
bottle.add(tagPivot);
/* The tag swings about its hole, so the rope to the hole can be built once */
function updateStrands() {
  bottle.updateMatrixWorld(true);
  const hole = new THREE.Vector3(0, 0.06, 0).applyMatrix4(tagPivot.matrixWorld);
  bottle.worldToLocal(hole);
  for (const side of [-1, 1]) {
    const mid = KNOT.clone().lerp(hole, 0.5); mid.x += side * 0.05; mid.z += 0.1; mid.y -= 0.04;
    const c = new THREE.CatmullRomCurve3([KNOT.clone().add(new THREE.Vector3(side * 0.04, -0.03, 0)), mid, hole.clone().add(new THREE.Vector3(side * 0.03, 0, side * 0.02))]);
    twine.add(rope(c, 0.11, 7, 60), fuzz(c, 35, 0.12));
  }
}
async function makeTag() {
  const img = await loadImg(canvas.dataset.tag);
  const map = new THREE.Texture(img); map.colorSpace = THREE.SRGBColorSpace; map.anisotropy = renderer.capabilities.getMaxAnisotropy(); map.needsUpdate = true;
  const mc = document.createElement("canvas"); mc.width = img.width; mc.height = img.height;
  const mg = mc.getContext("2d"); mg.drawImage(img, 0, 0); mg.globalCompositeOperation = "source-in"; mg.fillStyle = "#ffffff"; mg.fillRect(0, 0, mc.width, mc.height);
  const alphaMap = new THREE.CanvasTexture(mc);
  const geo = new THREE.PlaneGeometry(TAG_W, TAG_H, 12, 24);
  const p = geo.attributes.position;
  for (let i = 0; i < p.count; i++) { const u = p.getX(i) / TAG_W + 0.5, v = p.getY(i) / TAG_H + 0.5; p.setZ(i, Math.sin(u * Math.PI) * 0.06 + (1 - v) * 0.12); }
  geo.computeVertexNormals();
  geo.translate((0.5 - HOLE_U) * TAG_W, -(0.5 - HOLE_V) * TAG_H, 0);
  const front = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ map, alphaMap, alphaTest: 0.5, roughness: 0.82, side: THREE.FrontSide }));
  const back = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: PAPER, alphaMap, alphaTest: 0.5, roughness: 0.9, side: THREE.BackSide }));
  tagPivot.add(front, back);
}
makeTag().catch(() => {});
updateStrands();

bottle.position.y = -7.63 / 2;

/* ---------- Pour: stream, splashes, ripples ---------- */
const streamMat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0, transmission: 1, thickness: 0.3, ior: 1.47, attenuationColor: OIL_DEEP, attenuationDistance: 0.25, envMapIntensity: 1.4 });
const stream = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.06, 1, 24, 1, true).translate(0, 0.5, 0), streamMat);
stream.visible = false; bottle.add(stream);
const drops = Array.from({ length: 16 }, () => { const d = new THREE.Mesh(new THREE.SphereGeometry(0.045, 12, 8), streamMat); d.visible = false; d.userData.v = new THREE.Vector3(); bottle.add(d); return d; });
const ripples = Array.from({ length: 3 }, (_, i) => {
  const r = new THREE.Mesh(new THREE.TorusGeometry(1, 0.012, 6, 96), new THREE.MeshBasicMaterial({ color: OIL_LIGHT, transparent: true, opacity: 0 }));
  r.rotation.x = Math.PI / 2; r.userData.phase = i / 3; bottle.add(r); return r;
});

const T = { open: 0.8, streamIn: 0.6, fillFrom: 1.1, fillTo: 4.1, streamOut: 4.1, closeFrom: 4.7, closeTo: 5.4 };
const TOP_Y = 13, G = 30, LID_OPEN = -2.0;
const pour = { active: false, t0: 0, fill: 0 }; // empty glass until a store button is clicked
const clamp01 = (x) => Math.min(1, Math.max(0, x));
const easeOut = (x) => 1 - Math.pow(1 - x, 3);
const easeOutBack = (x) => { const c = 1.9; return 1 + (c + 1) * Math.pow(x - 1, 3) + c * Math.pow(x - 1, 2); };
function playFill() {
  if (reduce) { pour.fill = 1; return; }
  pour.active = true; pour.t0 = performance.now() / 1000; pour.fill = 0;
}
addEventListener("tatvamazi:fill", playFill);
if (location.hash === "#full") pour.fill = 1; // preview link that shows the bottle already filled

function updatePour(dt) {
  let lidAngle = 0, pouring = false;
  if (pour.active) {
    const e = performance.now() / 1000 - pour.t0;
    if (e < T.closeFrom) lidAngle = LID_OPEN * easeOut(clamp01(e / T.open));
    else lidAngle = LID_OPEN * (1 - easeOutBack(clamp01((e - T.closeFrom) / (T.closeTo - T.closeFrom))));
    const u = clamp01((e - T.fillFrom) / (T.fillTo - T.fillFrom));
    pour.fill = 1 - Math.pow(1 - u, 2);
    const surf = OIL_BASE + pour.fill * OIL_H;
    const fall = (s) => TOP_Y - 0.5 * G * s * s;
    const bottom = e > T.streamIn ? Math.max(surf, fall(e - T.streamIn)) : TOP_Y;
    const top = e > T.streamOut ? Math.max(surf, fall(e - T.streamOut)) : TOP_Y;
    stream.visible = e > T.streamIn && top - bottom > 0.02;
    if (stream.visible) { stream.position.y = bottom; stream.scale.y = top - bottom; const w = 1 + Math.sin(e * 38) * 0.12; stream.scale.x = stream.scale.z = w; }
    pouring = stream.visible && bottom <= surf + 0.001;
    if (e > T.closeTo) { pour.active = false; pour.fill = 1; stream.visible = false; lidAngle = 0; }
  }
  lidPivot.rotation.x = lidAngle;
  oil.visible = pour.fill > 0.004;
  baseGlow.visible = oil.visible;
  oil.scale.y = Math.max(pour.fill, 0.001);
  const surf = OIL_BASE + pour.fill * OIL_H;
  for (const d of drops) {
    if (d.visible) { d.userData.v.y -= 9 * dt; d.position.addScaledVector(d.userData.v, dt); if (d.position.y < surf) d.visible = false; }
    else if (pouring && Math.random() < 0.08) {
      const a = Math.random() * Math.PI * 2, s = 0.3 + Math.random() * 0.5;
      d.position.set(0, surf + 0.02, 0); d.userData.v.set(Math.cos(a) * s, 1.2 + Math.random() * 1.2, Math.sin(a) * s); d.visible = true;
    }
  }
  const now = performance.now() / 1000;
  for (const r of ripples) {
    const k = (now * 0.9 + r.userData.phase) % 1;
    r.position.y = surf + 0.01; r.scale.setScalar(0.1 + k * 1.25);
    r.material.opacity = pouring ? (1 - k) * 0.55 : Math.max(0, r.material.opacity - dt * 1.5);
  }
}

/* Halo rings echo the circle of the logo */
/* Two open gold arcs, each led by a glowing point, so the circling motion is easy to see */
const haloMat = new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity: 0.42, blending: THREE.AdditiveBlending, depthWrite: false });
const ARC1 = Math.PI * 1.45, ARC2 = Math.PI * 1.05;
const halo1 = new THREE.Mesh(new THREE.TorusGeometry(4.4, 0.012, 8, 260, ARC1), haloMat);
const halo2 = new THREE.Mesh(new THREE.TorusGeometry(5.0, 0.009, 8, 260, ARC2), haloMat.clone());
const glowDot = gradientTexture(64, 64, 32, 32, 32, [[0, "rgba(248,190,113,1)"], [0.35, "rgba(248,190,113,0.5)"], [1, "rgba(248,190,113,0)"]]);
for (const [ring, r, a, s] of [[halo1, 4.4, ARC1, 0.55], [halo2, 5.0, ARC2, 0.4]]) {
  const dot = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowDot, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
  dot.position.set(Math.cos(a) * r, Math.sin(a) * r, 0); dot.scale.setScalar(s); ring.add(dot);
}
halo1.position.z = -3.4; halo2.position.z = -4.2;
pivot.add(halo1, halo2);

/* Gold dust */
const N = 420, pos = new Float32Array(N * 3), seed = new Float32Array(N);
for (let i = 0; i < N; i++) { pos[i * 3] = (Math.random() - 0.5) * 16; pos[i * 3 + 1] = (Math.random() - 0.5) * 10; pos[i * 3 + 2] = (Math.random() - 0.5) * 6 - 1; seed[i] = Math.random() * 10; }
const dustGeo = new THREE.BufferGeometry(); dustGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
const dust = new THREE.Points(dustGeo, new THREE.PointsMaterial({
  size: 0.06, map: gradientTexture(64, 64, 32, 32, 32, [[0, "rgba(248,190,113,1)"], [1, "rgba(248,190,113,0)"]]),
  transparent: true, opacity: 0.45, depthWrite: false, blending: THREE.AdditiveBlending
}));
scene.add(dust);

/* ---------- Choreography: the bottle travels to whichever slot is nearest the middle of the screen ---------- */
const slots = [...document.querySelectorAll("[data-slot]")];
const BOTTLE_H = 7.8;
const cur = { x: 0, y: 0, s: 1, spin: 0 }, tgt = { ...cur };
let W = 0, H = 0, mouseX = 0, mouseY = 0, first = true;
function worldPerPx() { return (2 * CAM_Z * Math.tan(THREE.MathUtils.degToRad(FOV / 2))) / H; }
function pickTarget() {
  let best = null, bestD = Infinity;
  for (const el of slots) { const r = el.getBoundingClientRect(); const d = Math.abs(r.top + r.height / 2 - H / 2); if (d < bestD) { bestD = d; best = { el, r }; } }
  if (!best) return;
  const { el, r } = best, k = worldPerPx();
  tgt.x = (r.left + r.width / 2 - W / 2) * k;
  tgt.y = -(r.top + r.height / 2 - H / 2) * k;
  tgt.s = Math.min((r.height * 0.84 * k) / BOTTLE_H, (r.width * 1.25 * k) / BOTTLE_H);
  tgt.spin = Number(el.dataset.spin) * Math.PI * 2;
  const narrow = W < 860, inSlot = r.top < H * 0.75 && r.bottom > H * 0.25;
  canvas.style.opacity = narrow && !inSlot ? 0.28 : 1;
  if (first) { Object.assign(cur, tgt); first = false; }
}
function resize() { W = innerWidth; H = innerHeight; renderer.setSize(W, H, false); camera.aspect = W / H; camera.updateProjectionMatrix(); pickTarget(); }
addEventListener("resize", resize);
addEventListener("scroll", pickTarget, { passive: true });
addEventListener("pointermove", (e) => { mouseX = e.clientX / W - 0.5; mouseY = e.clientY / H - 0.5; }, { passive: true });
resize();

const clock = new THREE.Clock();
let last = 0;
function frame() {
  const t = clock.getElapsedTime();
  const dt = Math.min(0.05, t - last); last = t;
  if (!pour.active && pour.fill === 0 && location.hash === "#full") pour.fill = 1;
  updatePour(dt);
  const ease = reduce ? 1 : 0.075;
  for (const k of ["x", "y", "s", "spin"]) cur[k] += (tgt[k] - cur[k]) * ease;
  pivot.position.set(cur.x, cur.y + (reduce ? 0 : Math.sin(t * 0.9) * 0.05), 0);
  pivot.scale.setScalar(cur.s);
  const idle = reduce || pour.active ? 0 : Math.sin(t * 0.5) * 0.22;
  bottle.rotation.y = cur.spin + idle + mouseX * 0.45;
  bottle.rotation.x = mouseY * 0.1;
  /* the tag swings gently on its twine */
  tagPivot.rotation.z = 0.1 + (reduce ? 0 : Math.sin(t * 1.3) * 0.03);
  tagPivot.rotation.x = reduce ? 0.04 : 0.04 + Math.sin(t * 0.9 + 1) * 0.025;
  /* the two arcs circle the bottle in opposite directions */
  const spin = reduce ? 0 : t;
  halo1.rotation.set(0.12, 0, -spin * 0.42);
  halo2.rotation.set(-0.1, 0, spin * 0.26 + 1.8);
  const pulse = reduce ? 1 : 0.8 + Math.sin(t * 1.6) * 0.2;
  halo1.material.opacity = 0.42 * pulse; halo2.material.opacity = 0.24 * pulse;
  if (!reduce) {
    const p = dustGeo.attributes.position.array;
    for (let i = 0; i < N; i++) { p[i * 3 + 1] += 0.0025 + Math.sin(t + seed[i]) * 0.0008; p[i * 3] += Math.cos(t * 0.3 + seed[i]) * 0.0015; if (p[i * 3 + 1] > 5) p[i * 3 + 1] = -5; }
    dustGeo.attributes.position.needsUpdate = true;
  }
  renderer.render(scene, camera);
  requestAnimationFrame(frame);
}
frame();
